/* eslint-disable no-nested-ternary */
import React from 'react';
import { TableBodyPropGetter, TableBodyProps, Row, Cell } from 'react-table';
import clsx from 'clsx';
import { isValidKeyboardEventKey } from 'Utils';
import { Popover } from '@brighterion/br-ui';
import RowCell from './RowCell';
import styles from './TBody.css';
import { OurTableProps } from '../types';

interface Props<D extends object> extends Pick<OurTableProps<D>, 'conditionalRowClasses'> {
    getTableBodyProps: (propGetter?: TableBodyPropGetter<D> | undefined) => TableBodyProps;
    rows: Row<D>[];
    prepareRow: (row: Row<D>) => void;
    onRowInteract?: (row: Row<D>, event: React.KeyboardEvent | React.MouseEvent) => void;
    inactiveRowProps?: {
        inactiveRowFn?: (row: Row<D>) => boolean;
        inactiveRowHoverMsg?: (row: Row<D>) => string;
    };
    handleEditRow?: Function;
    tableName?: string;
    rulesGroupData?: RuleGroup[];
}

const TBody = <D extends object = {}>({
    getTableBodyProps,
    rows,
    prepareRow,
    onRowInteract,
    conditionalRowClasses,
    inactiveRowProps,
    handleEditRow,
    tableName,
    rulesGroupData
}: Props<D>) => {
    const handleKey = (event: React.KeyboardEvent, row: Row<D>) => {
        if (isValidKeyboardEventKey(event) && onRowInteract) {
            onRowInteract(row, event);
        }
    };

    const handleClick = (event: React.MouseEvent, row: Row<D>) => {
        event.preventDefault();
        if (onRowInteract) {
            onRowInteract(row, event);
        }
    };

    const renderRows = (row: Row<D>) => {
        return (
            <>
                {row.cells.map((cell: Cell<D, any>) => {
                    return (
                        <RowCell
                            conditionalRowClasses={conditionalRowClasses}
                            key={cell.getCellProps().key}
                            cell={cell}
                            row={row}
                            inactiveRowFn={inactiveRowProps?.inactiveRowFn}
                            handleEditRow={handleEditRow}
                            tableName={tableName}
                            rulesGroupData={rulesGroupData}
                        />
                    );
                })}
            </>
        );
    };

    return (
        <div className='tbody' {...getTableBodyProps()}>
            {rows.map((row) => {
                prepareRow(row);
                const { key: rKey, ...rowProps } = row.getRowProps();
                const isRowSplitOrExpanded = row?.isExpanded || row?.isSplit;
                const isGroupBy = row.cells.some((cell) => cell.isGrouped);
                const displayDisabledRowPopover =
                    inactiveRowProps?.inactiveRowFn &&
                    inactiveRowProps.inactiveRowFn(row) &&
                    inactiveRowProps?.inactiveRowHoverMsg &&
                    inactiveRowProps.inactiveRowHoverMsg(row) !== '';

                const interactionStuff = isGroupBy
                    ? {}
                    : {
                          tabIndex: 0,
                          onClick: (event: React.MouseEvent) => handleClick(event, row),
                          onKeyUp: (event: React.KeyboardEvent<HTMLDivElement>) => handleKey(event, row)
                      };
                return (
                    <div
                        data-testid={`tbody-row-${row.id}`}
                        data-isgrouped={row.isGrouped}
                        className={clsx(
                            'tr',
                            isRowSplitOrExpanded && 'selectedRow',
                            onRowInteract && 'cursorPointer',
                            isGroupBy && styles.groupBy
                        )}
                        role='row'
                        key={rKey}
                        {...rowProps}
                        {...interactionStuff}
                    >
                        {displayDisabledRowPopover ? (
                            <Popover
                                contentClassName={styles.popoverContent}
                                content={inactiveRowProps?.inactiveRowHoverMsg?.(row)}
                                isAltText
                                position='bottom'
                            >
                                {renderRows(row)}
                            </Popover>
                        ) : (
                            <>{renderRows(row)}</>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default TBody;
